import { getCountrySpecifications } from "ibantools";

export type BankFieldKey =
  | "accountNumber"
  | "sortCode"
  | "routingNumber"
  | "swift"
  | "iban";

export interface BankAccountFields
  extends Partial<Record<BankFieldKey, string | null>> {
  bankCountry?: string | null;
}

export interface GetBankFieldRequirementsArgs {
  country?: string | null;
  currency?: string | null;
}

let ibanCountries: Set<string> | null = null;

const isIbanCountry = (country?: string | null): boolean => {
  if (!country) return false;
  if (!ibanCountries) {
    const specs = getCountrySpecifications();
    ibanCountries = new Set(
      Object.keys(specs).filter((code) => specs[code].IBANRegistry)
    );
  }
  return ibanCountries.has(country.toUpperCase());
};

// ibantools lists Jersey, Guernsey and the Isle of Man with no IBAN registry, but their banks issue GB IBANs.
const CROWN_DEPENDENCIES = new Set(["GG", "IM", "JE"]);

/** Country decides how an account is identified; currency splits the UK row only, because BACS is GBP-only. */
export const getBankFieldRequirements = ({
  country,
  currency,
}: GetBankFieldRequirementsArgs): BankFieldKey[] => {
  const ctry = country?.trim().toUpperCase();
  const cur = currency?.trim().toUpperCase();

  if (ctry === "GB") {
    // No recorded currency is treated as GBP, the dominant case for UK accounts.
    return !cur || cur === "GBP"
      ? ["sortCode", "accountNumber"]
      : ["iban", "swift"];
  }
  if (ctry === "US") {
    return ["routingNumber", "accountNumber", "swift"];
  }
  if (isIbanCountry(ctry) || (ctry && CROWN_DEPENDENCIES.has(ctry))) {
    return ["iban", "swift"];
  }
  return ["accountNumber", "swift"];
};

/** Every field set that identifies an account, starting with the one forms ask for. */
export const getAcceptedBankFieldSets = ({
  country,
  currency,
}: GetBankFieldRequirementsArgs): BankFieldKey[][] => {
  const asked = getBankFieldRequirements({ country, currency });
  const ctry = country?.trim().toUpperCase();
  const cur = currency?.trim().toUpperCase();
  // Older Crown Dependency accounts were verified on a UK sort code and account number, which BACS pays in GBP only.
  const takesSortCode =
    !!ctry && CROWN_DEPENDENCIES.has(ctry) && (!cur || cur === "GBP");
  return takesSortCode ? [asked, ["sortCode", "accountNumber"]] : [asked];
};

export interface GetPayableBankFieldsArgs extends GetBankFieldRequirementsArgs {
  account: Partial<Record<BankFieldKey, string | null>>;
}

/** The fields this account is paid on: an accepted set it holds in full, otherwise the set forms ask for. */
export const getPayableBankFields = ({
  account,
  ...rule
}: GetPayableBankFieldsArgs): BankFieldKey[] => {
  const [asked, ...alternatives] = getAcceptedBankFieldSets(rule);
  return (
    alternatives.find((set) =>
      set.every((field) => !!account[field]?.trim())
    ) ?? asked
  );
};

export const findMissingBankFields = (
  args: GetPayableBankFieldsArgs
): BankFieldKey[] =>
  getPayableBankFields(args).filter((field) => !args.account[field]?.trim());

/** Keyed on the currency the money pays out in, not the currency the account holds. */
export const canReceiveCurrency = (
  account: BankAccountFields,
  currency: string
): boolean =>
  findMissingBankFields({
    country: account.bankCountry,
    currency,
    account,
  }).length === 0;
